import { useEffect } from "react";
import {
  DEFAULT_LANGUAGE,
  LOCALES,
} from "@modules/shared/localization/const/language";
import { Devtools } from "@modules/shared/Providers/Devtools";
import appCss from "@styles/index.css?url";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  redirect,
  useParams,
} from "@tanstack/react-router";
import { seo } from "@utils/seo";
import { getCookie, getLocaleCookie, setCookie } from "@utils/cookies";
import { LEGACY_REDIRECTS } from "@utils/legacyRedirects";
import i18next from "@modules/shared/localization/i18n";
import { LANGUAGE_KEY, USER_ACCESS_TOKEN_KEY } from "../../consts/keys";
import type { QueryClient } from "@tanstack/react-query";
import { NotFound } from "@/defaults/NotFound";
import { DefaultCatchBoundary } from "@/defaults/DefaultCatchBoundary";

export const Route = createRootRouteWithContext<{
  isAuthenticated: () => boolean;
  queryClient: QueryClient;
}>()({
  component: RootComponent,
  ssr: true,
  errorComponent: (props) => {
    return (
      <RootDocument>
        <DefaultCatchBoundary {...props} />
      </RootDocument>
    );
  },
  notFoundComponent: () => <NotFound />,
  beforeLoad({ params, location }) {
    // Skip locale check for system routes (OAuth callback, etc.)
    if (location.pathname.startsWith("/auth/")) {
      return { accessToken: undefined };
    }

    if (
      !("locale" in params) ||
      !LOCALES.includes(params.locale as (typeof LOCALES)[number])
    ) {
      // Preserve the user's previously selected locale from the cookie,
      // falling back to DEFAULT_LANGUAGE only if no cookie exists.
      const savedLocale = getLocaleCookie();
      const targetLocale =
        savedLocale && LOCALES.includes(savedLocale as (typeof LOCALES)[number])
          ? savedLocale
          : DEFAULT_LANGUAGE;

      // Sticky cookie: only set when missing/changed so Cloud CDN can cache
      // responses (any Set-Cookie downgrades cache-control public -> private).
      if (savedLocale !== targetLocale) {
        setCookie(LANGUAGE_KEY, targetLocale);
      }
      const legacyPath =
        LEGACY_REDIRECTS[location.pathname.replace(/\/$/, "").toLowerCase()];
      if (legacyPath !== undefined) {
        throw redirect({
          href: `/${targetLocale}${legacyPath}`,
          statusCode: 301,
        });
      }

      // Keep the path so locale-less links (/info/...) open that page in the
      // visitor's language instead of the homepage.
      const path = location.pathname === "/" ? "" : location.pathname;
      throw redirect({
        href: `/${targetLocale}${path}${location.searchStr}`,
        replace: true,
      });
    }

    const savedLocale = getLocaleCookie();
    const locale = params.locale as (typeof LOCALES)[number];

    // A locale-prefixed URL is an explicit language choice. Persist it
    // immediately so later visits to `/` redirect to the same locale.
    if (savedLocale !== locale) {
      setCookie(LANGUAGE_KEY, locale);
    }

    const accessToken = getCookie(USER_ACCESS_TOKEN_KEY);
    return { accessToken };
  },
  head: () => ({
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "96x96",
        href: "/favicon-96x96.png",
      },
      { rel: "manifest", href: "/site.webmanifest", color: "#ffffff" },
      { rel: "icon", href: "/favicon.ico" },
    ],
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      ...seo({
        title: "Postolar Varga",
      }),
    ],
  }),
});

function RootComponent() {
  return (
    <RootDocument>
      <LocaleCookieSync />
      <Outlet />
    </RootDocument>
  );
}

/**
 * Client-side fallback for already-hydrated navigations between locale routes.
 * Server-side route loading also persists explicit locale-prefixed visits.
 */
function LocaleCookieSync() {
  const params = useParams({ strict: false }) as { locale?: string };
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!params.locale) return;
    if (!LOCALES.includes(params.locale as (typeof LOCALES)[number])) return;
    const current = getLocaleCookie();
    if (current !== params.locale) {
      setCookie(LANGUAGE_KEY, params.locale);
    }
  }, [params.locale]);
  return null;
}

function RootDocument({ children }: { children: React.ReactNode }) {
  const params = useParams({ strict: false }) as { locale?: string };
  const lang = params.locale || i18next.language;
  return (
    <html lang={lang || "en"}>
      <head>
        {import.meta.env.MODE !== "production" && (
          <script src="https://unpkg.com/react-scan/dist/auto.global.js" />
        )}
        <HeadContent />
      </head>
      <body id="root">
        {children}
        {import.meta.env.MODE !== "production" && <Devtools />}
        <Scripts />
      </body>
    </html>
  );
}
